// src/pages/admin/AdminDashboard.js
import { useState, useEffect, useRef } from "react";
import { useAuth } from "../../context/AuthContext";
import { useNavigate } from "react-router-dom";
import {
  getProjects, addProject, updateProject, deleteProject,
  getExperiences, addExperience, updateExperience, deleteExperience,
  getSkills, addSkill, updateSkill, deleteSkill,
  getCertificates, addCertificate, updateCertificate, deleteCertificate,
  getServices, addService, updateService, deleteService,
  getTestimonials, addTestimonial, updateTestimonial, deleteTestimonial,
  getSettings, saveSettings, updateOrder, seedData, getMessages
} from "../../lib/firestore";
import Terminal from "../../components/public/Terminal";
import "../../styles/admin.css";

// ── Modal ─────────────────────────────────────────────────
function Modal({ title, fields, values, onChange, onSave, onClose }) {
  return (
    <div className="admin-modal" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="admin-modal__box">
        <div className="admin-modal__title">{title}</div>
        <div className="admin-modal__grid">
          {fields.map(f => (
            <div key={f.key} className={`admin-modal__field${f.full ? " admin-modal__field--full" : ""}`}>
              <label className="admin-field-label">{f.label}</label>
              {f.type === "textarea"
                ? <textarea value={values[f.key] || ""} onChange={e => onChange(f.key, e.target.value)} rows={3} style={{ width: "100%" }} />
                : f.type === "select"
                  ? <select value={values[f.key] || ""} onChange={e => onChange(f.key, e.target.value)} style={{ width: "100%", padding: "10px 14px" }}>
                    {f.options.map(o => <option key={o.v} value={o.v}>{o.l}</option>)}
                  </select>
                  : <input type={f.type || "text"} value={values[f.key] || ""} onChange={e => onChange(f.key, e.target.value)} placeholder={f.placeholder || ""} style={{ width: "100%" }} />
              }
            </div>
          ))}
        </div>
        <div className="admin-modal__actions">
          <button className="admin-btn-cancel" onClick={onClose}>İptal</button>
          <button className="admin-btn-save" onClick={onSave}>Kaydet</button>
        </div>
      </div>
    </div>
  );
}

// ── Draggable List ────────────────────────────────────────
function DraggableList({ items, setItems, collectionName, renderRow }) {
  const dragIdx = useRef(null);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState("");

  const onDragStart = i => { dragIdx.current = i; };
  const onDragOver = (e, i) => {
    e.preventDefault();
    if (dragIdx.current === null || dragIdx.current === i) return;
    const next = [...items];
    const [m] = next.splice(dragIdx.current, 1);
    next.splice(i, 0, m);
    dragIdx.current = i;
    setItems(next);
  };
  const onDragEnd = async () => {
    dragIdx.current = null;
    setSaving(true);
    await updateOrder(collectionName, items);
    setSaving(false);
    setToast("Sıralama kaydedildi ✓");
    setTimeout(() => setToast(""), 2200);
  };

  return (
    <div>
      {toast && <div className="admin-info-box">{toast}</div>}
      {saving && <div style={{ fontSize: 11, color: "var(--muted)", marginBottom: 10 }}>Kaydediliyor...</div>}
      <div className="admin-table-wrap">
        <table className="admin-table">
          <thead>
            <tr>
              <th style={{ width: 36 }} />
              {renderRow.headers.map(h => <th key={h}>{h}</th>)}
              <th>İşlem</th>
            </tr>
          </thead>
          <tbody>
            {items.map((item, i) => (
              <tr key={item.id || i} draggable
                onDragStart={() => onDragStart(i)}
                onDragOver={e => onDragOver(e, i)}
                onDragEnd={onDragEnd}
                style={{ background: i % 2 === 0 ? "var(--card)" : "#111" }}
              >
                <td style={{ padding: "0 4px" }}>
                  <span className="admin-drag-handle" title="Sürükle">⠿</span>
                </td>
                {renderRow.cells(item).map((cell, ci) => <td key={ci}>{cell}</td>)}
                <td>
                  <button className="admin-action-btn admin-action-btn--edit" onClick={() => renderRow.onEdit(item)}>Düzenle</button>
                  <button className="admin-action-btn admin-action-btn--delete" onClick={() => { if (window.confirm("Silmek istediğinizden emin misiniz?")) renderRow.onDelete(item.id); }}>Sil</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ── CRUD sections ─────────────────────────────────────────
const ProjectsSection = () => {
  const [items, setItems] = useState([]);
  const [modal, setModal] = useState(null);
  const [form, setForm] = useState({});
  const load = () => getProjects().then(setItems);
  useEffect(() => { load(); }, []);
  const fields = [
    { key: "name_tr", label: "İsim (TR)" }, { key: "name_en", label: "Name (EN)" },
    { key: "url", label: "URL", placeholder: "https://...", full: true },
    { key: "tag", label: "Etiket", placeholder: "React" },
  ];
  const save = async () => {
    const d = { ...form, order: modal === "add" ? items.length + 1 : form.order };
    if (modal === "add") await addProject(d); else await updateProject(d.id, d);
    setModal(null); load();
  };
  return (
    <div>
      <div className="admin-info-box">Sıralamayı değiştirmek için satırları sürükleyip bırakın.</div>
      <button className="admin-add-btn" onClick={() => { setForm({}); setModal("add"); }}>+ Proje Ekle</button>
      <DraggableList items={items} setItems={setItems} collectionName="projects"
        renderRow={{ headers: ["İsim (TR)", "URL", "Etiket"], cells: r => [r.name_tr || "—", r.url ? <a href={r.url} target="_blank" rel="noreferrer" style={{ color: "var(--accent)", fontSize: 11 }}>{r.url.replace("https://", "")}</a> : "—", r.tag || "—"], onEdit: r => { setForm(r); setModal("edit"); }, onDelete: async id => { await deleteProject(id); load(); } }}
      />
      {modal && <Modal title={modal === "add" ? "Proje Ekle" : "Proje Düzenle"} fields={fields} values={form} onChange={(k, v) => setForm(f => ({ ...f, [k]: v }))} onSave={save} onClose={() => setModal(null)} />}
    </div>
  );
};

const ExperienceSection = () => {
  const [items, setItems] = useState([]);
  const [modal, setModal] = useState(null);
  const [form, setForm] = useState({});
  const load = () => getExperiences().then(setItems);
  useEffect(() => { load(); }, []);
  const fields = [
    { key: "role_tr", label: "Unvan (TR)" }, { key: "role_en", label: "Role (EN)" },
    { key: "company", label: "Şirket", full: true },
    { key: "date_tr", label: "Tarih (TR)", placeholder: "2022 — 2025" }, { key: "date_en", label: "Date (EN)" },
    { key: "desc_tr", label: "Açıklama (TR)", type: "textarea", full: true },
    { key: "desc_en", label: "Description (EN)", type: "textarea", full: true },
  ];
  const save = async () => {
    const d = { ...form, order: modal === "add" ? items.length + 1 : form.order };
    if (modal === "add") await addExperience(d); else await updateExperience(d.id, d);
    setModal(null); load();
  };
  return (
    <div>
      <div className="admin-info-box">Sıralamayı değiştirmek için satırları sürükleyip bırakın.</div>
      <button className="admin-add-btn" onClick={() => { setForm({}); setModal("add"); }}>+ Deneyim Ekle</button>
      <DraggableList items={items} setItems={setItems} collectionName="experiences"
        renderRow={{ headers: ["Unvan (TR)", "Şirket", "Tarih"], cells: r => [r.role_tr || "—", r.company || "—", r.date_tr || "—"], onEdit: r => { setForm(r); setModal("edit"); }, onDelete: async id => { await deleteExperience(id); load(); } }}
      />
      {modal && <Modal title={modal === "add" ? "Deneyim Ekle" : "Düzenle"} fields={fields} values={form} onChange={(k, v) => setForm(f => ({ ...f, [k]: v }))} onSave={save} onClose={() => setModal(null)} />}
    </div>
  );
};

const SkillsSection = () => {
  const [items, setItems] = useState([]);
  const [modal, setModal] = useState(null);
  const [form, setForm] = useState({});
  const load = () => getSkills().then(setItems);
  useEffect(() => { load(); }, []);
  const save = async () => {
    const d = { ...form, order: modal === "add" ? items.length + 1 : form.order };
    if (modal === "add") await addSkill(d); else await updateSkill(d.id, d);
    setModal(null); load();
  };
  return (
    <div>
      <div className="admin-info-box">Sıralamayı değiştirmek için satırları sürükleyip bırakın.</div>
      <button className="admin-add-btn" onClick={() => { setForm({}); setModal("add"); }}>+ Teknoloji Ekle</button>
      <DraggableList items={items} setItems={setItems} collectionName="skills"
        renderRow={{ headers: ["Teknoloji"], cells: r => [r.name || "—"], onEdit: r => { setForm(r); setModal("edit"); }, onDelete: async id => { await deleteSkill(id); load(); } }}
      />
      {modal && <Modal title={modal === "add" ? "Teknoloji Ekle" : "Düzenle"} fields={[{ key: "name", label: "Teknoloji Adı", full: true }]} values={form} onChange={(k, v) => setForm(f => ({ ...f, [k]: v }))} onSave={save} onClose={() => setModal(null)} />}
    </div>
  );
};

const CertificatesSection = () => {
  const [items, setItems] = useState([]);
  const [modal, setModal] = useState(null);
  const [form, setForm] = useState({});
  const load = () => getCertificates().then(setItems);
  useEffect(() => { load(); }, []);
  const fields = [
    { key: "issuer", label: "Kurum", full: true },
    { key: "name_tr", label: "Sertifika Adı (TR)" }, { key: "name_en", label: "Certificate Name (EN)" },
    { key: "url", label: "Sertifika Linki (opsiyonel)", placeholder: "https://...", full: true },
  ];
  const save = async () => {
    const d = { ...form, order: modal === "add" ? items.length + 1 : form.order };
    if (modal === "add") await addCertificate(d); else await updateCertificate(d.id, d);
    setModal(null); load();
  };
  return (
    <div>
      <div className="admin-info-box">Link eklenen sertifikalar sitede tıklanabilir olur.</div>
      <button className="admin-add-btn" onClick={() => { setForm({}); setModal("add"); }}>+ Sertifika Ekle</button>
      <DraggableList items={items} setItems={setItems} collectionName="certificates"
        renderRow={{ headers: ["Kurum", "Sertifika (TR)", "Link"], cells: r => [r.issuer || "—", r.name_tr || "—", r.url ? <span style={{ color: "var(--accent)" }}>✓</span> : <span style={{ color: "var(--muted)" }}>—</span>], onEdit: r => { setForm(r); setModal("edit"); }, onDelete: async id => { await deleteCertificate(id); load(); } }}
      />
      {modal && <Modal title={modal === "add" ? "Sertifika Ekle" : "Düzenle"} fields={fields} values={form} onChange={(k, v) => setForm(f => ({ ...f, [k]: v }))} onSave={save} onClose={() => setModal(null)} />}
    </div>
  );
};

const ServicesSection = () => {
  const [items, setItems] = useState([]);
  const [modal, setModal] = useState(null);
  const [form, setForm] = useState({});
  const load = () => getServices().then(setItems);
  useEffect(() => { load(); }, []);
  const fields = [
    { key: "title_tr", label: "Başlık (TR)" }, { key: "title_en", label: "Title (EN)" },
    { key: "icon", label: "İkon (sembol)", placeholder: "◈" },
    { key: "desc_tr", label: "Açıklama (TR)", type: "textarea", full: true },
    { key: "desc_en", label: "Description (EN)", type: "textarea", full: true },
  ];
  const save = async () => {
    const d = { ...form, order: modal === "add" ? items.length + 1 : form.order };
    if (modal === "add") await addService(d); else await updateService(d.id, d);
    setModal(null); load();
  };
  return (
    <div>
      <div className="admin-info-box">Hizmetler bölümü sitede "Hizmetler" başlığı altında gösterilir.</div>
      <button className="admin-add-btn" onClick={() => { setForm({ icon: "◈" }); setModal("add"); }}>+ Hizmet Ekle</button>
      <DraggableList items={items} setItems={setItems} collectionName="services"
        renderRow={{ headers: ["İkon", "Başlık (TR)", "Açıklama"], cells: r => [r.icon || "—", r.title_tr || "—", (r.desc_tr || "").slice(0, 50) + (r.desc_tr?.length > 50 ? "..." : "")], onEdit: r => { setForm(r); setModal("edit"); }, onDelete: async id => { await deleteService(id); load(); } }}
      />
      {modal && <Modal title={modal === "add" ? "Hizmet Ekle" : "Düzenle"} fields={fields} values={form} onChange={(k, v) => setForm(f => ({ ...f, [k]: v }))} onSave={save} onClose={() => setModal(null)} />}
    </div>
  );
};

const TestimonialsSection = () => {
  const [items, setItems] = useState([]);
  const [modal, setModal] = useState(null);
  const [form, setForm] = useState({});
  const load = () => getTestimonials().then(setItems);
  useEffect(() => { load(); }, []);
  const fields = [
    { key: "name", label: "İsim", full: true },
    { key: "company_tr", label: "Şirket (TR)" }, { key: "company_en", label: "Company (EN)" },
    { key: "text_tr", label: "Yorum (TR)", type: "textarea", full: true },
    { key: "text_en", label: "Comment (EN)", type: "textarea", full: true },
    { key: "link", label: "Site Linki", full: true },
  ];
  const save = async () => {
    const d = { ...form, order: modal === "add" ? items.length + 1 : form.order };
    if (modal === "add") await addTestimonial(d); else await updateTestimonial(d.id, d);
    setModal(null); load();
  };
  return (
    <div>
      <div className="admin-info-box">Referans yorumları sitede "Müşteriler Ne Diyor" bölümünde gösterilir.</div>
      <button className="admin-add-btn" onClick={() => { setForm({}); setModal("add"); }}>+ Referans Ekle</button>
      <DraggableList items={items} setItems={setItems} collectionName="testimonials"
        renderRow={{ headers: ["İsim", "Şirket", "Yorum", "Link"], cells: r => [r.name || "—", r.company_tr || "—", (r.text_tr || "").slice(0, 50) + (r.text_tr?.length > 50 ? "..." : ""), r.link || "—"], onEdit: r => { setForm(r); setModal("edit"); }, onDelete: async id => { await deleteTestimonial(id); load(); } }}
      />
      {modal && <Modal title={modal === "add" ? "Referans Ekle" : "Düzenle"} fields={fields} values={form} onChange={(k, v) => setForm(f => ({ ...f, [k]: v }))} onSave={save} onClose={() => setModal(null)} />}
    </div>
  );
};

// ── Messages ──────────────────────────────────────────────
function MessagesSection() {
  const [items, setItems] = useState([]);
  const [selected, setSelected] = useState(null);
  useEffect(() => { getMessages().then(msgs => setItems([...msgs].reverse())); }, []);
  return (
    <div>
      {selected && (
        <div className="admin-msg-detail">
          <div className="admin-msg-header">
            <div className="admin-msg-title">{selected.name}</div>
            <button className="admin-msg-close" onClick={() => setSelected(null)}>✕</button>
          </div>
          <div className="admin-msg-meta">
            {[["E-posta", selected.email], ["Şirket", selected.company || "—"], ["Bütçe", selected.budget || "—"], ["Dil", selected.lang || "tr"]].map(([l, v]) => (
              <div key={l}><div className="admin-msg-meta-label">{l}</div><div className="admin-msg-meta-val">{v}</div></div>
            ))}
          </div>
          <div className="admin-msg-meta-label">Mesaj</div>
          <div className="admin-msg-body">{selected.message}</div>
          <a href={`mailto:${selected.email}?subject=Re: Proje Talebi`} className="admin-msg-reply">Yanıtla →</a>
        </div>
      )}
      <div className="admin-table-wrap">
        <table className="admin-table">
          <thead><tr><th>İsim</th><th>E-posta</th><th>Bütçe</th><th>Tarih</th><th>İşlem</th></tr></thead>
          <tbody>
            {items.map((msg, i) => (
              <tr key={msg.id || i} style={{ background: i % 2 === 0 ? "var(--card)" : "#111", cursor: "pointer" }} onClick={() => setSelected(msg)}>
                <td>{msg.name || "—"}</td>
                <td><span style={{ color: "var(--accent)", fontSize: 11 }}>{msg.email || "—"}</span></td>
                <td>{msg.budget || "—"}</td>
                <td>{msg.createdAt?.toDate ? msg.createdAt.toDate().toLocaleDateString("tr") : "—"}</td>
                <td><button className="admin-action-btn admin-action-btn--edit" onClick={e => { e.stopPropagation(); setSelected(msg); }}>Görüntüle</button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ── Settings ──────────────────────────────────────────────

// ── Settings alt bileşenleri (SettingsSection DIŞINDA tanımlı — focus sorunu önlenir) ──
function SettingsField({ form, set, label, k, type = "text", full = false, placeholder = "" }) {
  return (
    <div className={`admin-settings-field${full ? " admin-settings-field--full" : ""}`}>
      {label && <label className="admin-field-label">{label}</label>}
      {type === "textarea"
        ? <textarea value={form[k] || ""} onChange={e => set(k, e.target.value)} rows={3} style={{ width: "100%" }} placeholder={placeholder} />
        : type === "toggle"
          ? <div className="admin-toggle-wrap">
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 11, fontWeight: 500, marginBottom: 4 }}>Müsaitlik Durumu</div>
              <div style={{ fontSize: 11, color: "var(--muted)" }}>Hero'da renkli badge olarak gösterilir</div>
            </div>
            <div className={`admin-toggle-track admin-toggle-track--${form[k] ? "on" : "off"}`} onClick={() => set(k, !form[k])}>
              <div className={`admin-toggle-thumb admin-toggle-thumb--${form[k] ? "on" : "off"}`} />
            </div>
          </div>
          : type === "color"
            ? <div className="admin-color-row">
              <input type="color" value={form[k] || "#e8ff47"} onChange={e => set(k, e.target.value)} className="admin-color-input" />
              <input type="text" value={form[k] || ""} onChange={e => set(k, e.target.value)} style={{ flex: 1 }} placeholder="#e8ff47" />
            </div>
            : <input type={type} value={form[k] || ""} onChange={e => set(k, e.target.value)} style={{ width: "100%" }} placeholder={placeholder} />
      }
    </div>
  );
}

function TerminalLinesEditor({ form, set, langKey, label }) {
  const lines = form[langKey] || [];
  const setLines = next => set(langKey, next);
  const update = (i, k, v) => { const a = [...lines]; a[i] = { ...a[i], [k]: v }; setLines(a); };
  const add = () => setLines([...lines, { cmd: "", out: "" }]);
  const remove = i => setLines(lines.filter((_, idx) => idx !== i));
  return (
    <div className="admin-settings-field--full" style={{ marginBottom: 14 }}>
      <label className="admin-field-label">{label}</label>
      <div className="admin-terminal-lines">
        {lines.map((line, i) => (
          <div key={i} className="admin-terminal-line" style={{ background: i % 2 === 0 ? "var(--card)" : "#111" }}>
            <div className="admin-terminal-line__cell">
              <div className="admin-terminal-mini-label" style={{ color: "var(--muted)" }}>KOMUT</div>
              <input value={line.cmd || ""} onChange={e => update(i, "cmd", e.target.value)} placeholder="ls -la" className="admin-terminal-input admin-terminal-input--cmd" />
            </div>
            <div className="admin-terminal-line__cell">
              <div className="admin-terminal-mini-label" style={{ color: "var(--muted)" }}>ÇIKTI</div>
              <input value={line.out || ""} onChange={e => update(i, "out", e.target.value)} placeholder="sonuç..." className="admin-terminal-input admin-terminal-input--out" />
            </div>
            <button className="admin-terminal-remove-btn" onClick={() => remove(i)}>✕</button>
          </div>
        ))}
        <button className="admin-terminal-add-btn" onClick={add}>+ Satır Ekle</button>
      </div>
      <div className="admin-terminal-hint">Her satır bir komut-çıktı çiftidir. Sırayla oynatılır.</div>
    </div>
  );
}

function ProcessEditor({ form, set, stepsKey, descsKey, label }) {
  const steps = form[stepsKey] || ["", "", "", ""];
  const descs = form[descsKey] || ["", "", "", ""];
  return (
    <div className="admin-settings-field--full" style={{ marginBottom: 14 }}>
      <label className="admin-field-label">{label}</label>
      {steps.map((_, i) => (
        <div key={i} className="admin-process-step">
          <div>
            <div className="admin-process-label admin-process-label--title">ADIM {i + 1} BAŞLIĞI</div>
            <input value={steps[i] || ""} onChange={e => { const a = [...steps]; a[i] = e.target.value; set(stepsKey, a); }} style={{ width: "100%" }} placeholder={`Adım ${i + 1}`} />
          </div>
          <div>
            <div className="admin-process-label admin-process-label--desc">AÇIKLAMA</div>
            <input value={descs[i] || ""} onChange={e => { const a = [...descs]; a[i] = e.target.value; set(descsKey, a); }} style={{ width: "100%" }} placeholder="Kısa açıklama..." />
          </div>
        </div>
      ))}
    </div>
  );
}

function SettingsSection() {
  const [form, setForm] = useState(null);
  const [saved, setSaved] = useState(false);
  const [tab, setTab] = useState("general");
  const [preview, setPreview] = useState(null);

  useEffect(() => { getSettings().then(s => setForm(s || {})); }, []);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const updatePreview = () => setPreview({ ...form });
  const handleSave = async () => { await saveSettings(form); setSaved(true); setTimeout(() => setSaved(false), 2500); };

  if (!form) return <div style={{ color: "var(--muted)", fontSize: 12 }}>Yükleniyor...</div>;

  const TABS = [
    { id: "general", label: "Genel" }, { id: "hero", label: "Hero Metinleri" },
    { id: "terminal", label: "Terminal" }, { id: "cta", label: "CTA & Süreç" },
    { id: "contact", label: "İletişim" },
  ];


  return (
    <div>
      {saved && <div className="admin-saved-box">✓ Değişiklikler kaydedildi ve siteye yansıdı.</div>}

      <div className="admin-tabs">
        {TABS.map(tb => (
          <button key={tb.id} className={`admin-tab${tab === tb.id ? " admin-tab--active" : ""}`} onClick={() => setTab(tb.id)}>
            {tb.label}
          </button>
        ))}
      </div>

      <div className="admin-settings-grid">
        {tab === "general" && <>
          <SettingsField form={form} set={set} label="Ad Soyad" k="name" full />
          <SettingsField form={form} set={set} label="Unvan (TR)" k="title_tr" />
          <SettingsField form={form} set={set} label="Title (EN)" k="title_en" />
          <SettingsField form={form} set={set} label="Yıl deneyim göstergesi" k="years_exp" placeholder="5+" />
          <SettingsField form={form} set={set} label="Şirket sayısı" k="companies" placeholder="3" />
          <SettingsField form={form} set={set} label="" k="available" type="toggle" full />
          <SettingsField form={form} set={set} label="Müsaitlik metni (TR)" k="available_text_tr" placeholder="Yeni projelere açığım" />
          <SettingsField form={form} set={set} label="Availability text (EN)" k="available_text_en" placeholder="Available for new projects" />
        </>}

        {tab === "hero" && <>
          <SettingsField form={form} set={set} label="Hero açıklaması (TR)" k="desc_tr" type="textarea" full placeholder="Kendinizi Türkçe tanıtın..." />
          <SettingsField form={form} set={set} label="Hero description (EN)" k="desc_en" type="textarea" full placeholder="Introduce yourself in English..." />
        </>}

        {tab === "terminal" && <>
          <div className="admin-terminal-layout">
            <div>
              <div style={{ marginBottom: 16 }}>
                <label className="admin-field-label">Renk Teması</label>
                <div className="admin-theme-selector">
                  {[{ v: "dark", l: "Dark", bg: "#0d0d0d", acc: "#e8ff47" }, { v: "light", l: "Light", bg: "#f5f5f0", acc: "#7a5c00" }, { v: "retro", l: "Retro", bg: "#0a0a00", acc: "#aaff00" }, { v: "nord", l: "Nord", bg: "#2e3440", acc: "#ebcb8b" }].map(th => (
                    <div key={th.v} className={`admin-theme-chip${form.terminal_theme === th.v ? " admin-theme-chip--active" : ""}`}
                      onClick={() => set("terminal_theme", th.v)}
                      style={{ background: th.bg, color: th.acc }}>
                      {th.l}
                    </div>
                  ))}
                </div>
              </div>
              <SettingsField form={form} set={set} label="Çıktı rengi" k="terminal_output_color" type="color" full />
              <SettingsField form={form} set={set} label="Başlık çubuğu metni" k="terminal_title" placeholder="terminal — dursun@portfolio" full />
              <SettingsField form={form} set={set} label="Kullanıcı adı" k="terminal_username" placeholder="dursun" />
              <SettingsField form={form} set={set} label="Hostname" k="terminal_hostname" placeholder="portfolio" />
              <div className="admin-settings-field--full" style={{ marginBottom: 14 }}>
                <label className="admin-field-label">Ekran görüntüsü bekleme süresi</label>
                <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 4 }}>
                  {[0, 1000, 2000, 3000, 4000, 5000].map(ms => (
                    <div key={ms} onClick={() => set("screenshot_wait", ms)}
                      style={{ padding: "7px 14px", fontSize: 11, cursor: "pointer", fontFamily: "'DM Mono',monospace", border: `1px solid ${(form.screenshot_wait ?? 2000) === ms ? "var(--accent)" : "var(--border)"}`, color: (form.screenshot_wait ?? 2000) === ms ? "var(--accent)" : "var(--muted)", background: "var(--card)", transition: "border-color 0.15s" }}>
                      {ms === 0 ? "Anında" : `${ms / 1000}sn`}
                    </div>
                  ))}
                </div>
                <div style={{ fontSize: 10, color: "var(--muted)", marginTop: 8 }}>Loader animasyonu olan sitelerde 2–3sn önerilir.</div>
              </div>
              <div className="admin-settings-field--full" style={{ marginBottom: 14 }}>
                <label className="admin-field-label">Yazma hızı ({form.terminal_typing_speed || 60}ms)</label>
                <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                  <span style={{ fontSize: 10, color: "var(--muted)" }}>Hızlı</span>
                  <input type="range" min={20} max={150} value={form.terminal_typing_speed || 60} onChange={e => set("terminal_typing_speed", Number(e.target.value))} style={{ flex: 1, accentColor: "var(--accent)" }} />
                  <span style={{ fontSize: 10, color: "var(--muted)" }}>Yavaş</span>
                </div>
              </div>
            </div>
            <div>
              <div className="admin-preview-header">
                <label className="admin-field-label">Önizleme</label>
                <button className="admin-preview-btn" onClick={updatePreview}>Önizlemeyi Güncelle</button>
              </div>
              <Terminal settings={preview || form} />
              {!preview && <div className="admin-preview-hint">Değişiklikler yaptıktan sonra butona basın.</div>}
            </div>
          </div>
          <TerminalLinesEditor form={form} set={set} langKey="terminal_lines_tr" label="Terminal Satırları (TR)" />
          <TerminalLinesEditor form={form} set={set} langKey="terminal_lines_en" label="Terminal Satırları (EN)" />
        </>}

        {tab === "cta" && <>
          <SettingsField form={form} set={set} label="CTA başlık (TR)" k="cta_title_tr" full placeholder="Projenizi Hayata Geçirelim" />
          <SettingsField form={form} set={set} label="CTA title (EN)" k="cta_title_en" full placeholder="Let's Build Your Project" />
          <SettingsField form={form} set={set} label="CTA açıklaması (TR)" k="cta_desc_tr" type="textarea" full />
          <SettingsField form={form} set={set} label="CTA description (EN)" k="cta_desc_en" type="textarea" full />
          <ProcessEditor form={form} set={set} stepsKey="process_steps_tr" descsKey="process_descs_tr" label="Çalışma Süreci Adımları (TR)" />
          <ProcessEditor form={form} set={set} stepsKey="process_steps_en" descsKey="process_descs_en" label="Work Process Steps (EN)" />
        </>}

        {tab === "contact" && <>
          <SettingsField form={form} set={set} label="E-posta" k="email" type="email" full />
          <SettingsField form={form} set={set} label="LinkedIn URL" k="linkedin" placeholder="https://linkedin.com/in/..." />
          <SettingsField form={form} set={set} label="GitHub URL" k="github" placeholder="https://github.com/..." />
          <SettingsField form={form} set={set} label="Instagram URL" k="instagram" placeholder="https://instagram.com/..." />
          <div className="admin-emailjs-box">
            <div className="admin-emailjs-box__title">E-posta Bildirimi (EmailJS)</div>
            <div className="admin-emailjs-box__desc">
              Form gönderilince kişisel mailinize bildirim gelmesi için{" "}
              <a href="https://emailjs.com" target="_blank" rel="noreferrer" style={{ color: "var(--accent)" }}>emailjs.com</a>'da ücretsiz hesap açın.
              Template değişkenleri: from_name, from_email, message, budget, company
            </div>
            <div className="admin-emailjs-grid">
              <div><label className="admin-field-label">Service ID</label><input value={form.emailjs_service_id || ""} onChange={e => set("emailjs_service_id", e.target.value)} placeholder="service_xxxxxxx" style={{ width: "100%" }} /></div>
              <div><label className="admin-field-label">Template ID</label><input value={form.emailjs_template_id || ""} onChange={e => set("emailjs_template_id", e.target.value)} placeholder="template_xxxxxxx" style={{ width: "100%" }} /></div>
              <div><label className="admin-field-label">Public Key</label><input value={form.emailjs_public_key || ""} onChange={e => set("emailjs_public_key", e.target.value)} placeholder="xxxxxxxxxxxx" style={{ width: "100%" }} /></div>
            </div>
          </div>
        </>}
      </div>

      <div style={{ marginTop: 32, paddingTop: 24, borderTop: "1px solid var(--border)" }}>
        <button className="admin-save-btn" onClick={handleSave}>Değişiklikleri Kaydet →</button>
      </div>
    </div>
  );
}

// ── NAV & Main ────────────────────────────────────────────
const NAV = [
  { id: "settings", label: "Genel Ayarlar", icon: "◎" },
  { id: "messages", label: "Mesajlar", icon: "✉" },
  { id: "projects", label: "Projeler", icon: "◈" },
  { id: "experience", label: "Deneyim", icon: "◉" },
  { id: "skills", label: "Teknolojiler", icon: "◇" },
  { id: "certificates", label: "Sertifikalar", icon: "◆" },
  { id: "services", label: "Hizmetler", icon: "◐" },
  { id: "testimonials", label: "Referanslar", icon: "❝" },
];

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [active, setActive] = useState("settings");
  const [seeding, setSeeding] = useState(false);
  const [sideOpen, setSideOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 992);

  useEffect(() => {
    const fn = () => { setIsMobile(window.innerWidth < 992); if (window.innerWidth >= 992) setSideOpen(false); };
    window.addEventListener("resize", fn);
    return () => window.removeEventListener("resize", fn);
  }, []);

  const handleNav = (id) => { setActive(id); setSideOpen(false); };
  const handleLogout = async () => { await logout(); navigate("/admin/login"); };
  const handleSeed = async () => {
    if (!window.confirm("Başlangıç verisi yüklenecek. Devam?")) return;
    setSeeding(true);
    try { await seedData(); alert("Veriler yüklendi! Sayfayı yenileyin."); }
    catch (e) { alert("Hata: " + e.message); }
    setSeeding(false);
  };

  const currentLabel = NAV.find(n => n.id === active)?.label;

  return (
    <div className="admin-wrap">
      {/* Mobile overlay */}
      {isMobile && sideOpen && (
        <div className="admin-overlay admin-overlay--visible" onClick={() => setSideOpen(false)} />
      )}

      {/* Sidebar */}
      <aside className={`admin-sidebar${sideOpen ? " admin-sidebar--open" : ""}`}>
        <div className="admin-sidebar__top">
          <div>
            <div className="admin-sidebar__logo">D<span className="admin-sidebar__logo-accent">.</span>İNEL</div>
            <div className="admin-sidebar__label">Admin Panel</div>
          </div>
          <button className="admin-sidebar__close" onClick={() => setSideOpen(false)}>✕</button>
        </div>

        <nav className="admin-sidebar__nav">
          {NAV.map(s => (
            <button key={s.id} className={`admin-nav-item${active === s.id ? " admin-nav-item--active" : ""}`} onClick={() => handleNav(s.id)}>
              <span className="admin-nav-item__icon">{s.icon}</span>
              <span>{s.label}</span>
            </button>
          ))}
        </nav>

        <div className="admin-sidebar__bottom">
          <div className="admin-sidebar__email">{user?.email}</div>
          <button className="admin-sidebar__btn" onClick={() => navigate("/")}>← Siteye Dön</button>
          <button className="admin-sidebar__btn admin-sidebar__btn--danger" onClick={handleLogout}>Çıkış Yap</button>
        </div>
      </aside>

      {/* Mobile topbar */}
      {isMobile && (
        <div className="admin-topbar">
          <a href="/" className="admin-topbar__logo">D<span style={{ color: "var(--accent)" }}>.</span>İNEL</a>
          <div className="admin-topbar__right">
            <span className="admin-topbar__page">{currentLabel}</span>
            <button className={`admin-hamburger${sideOpen ? " admin-hamburger--open" : ""}`} onClick={() => setSideOpen(o => !o)} aria-label="Menü">
              <span className="admin-hamburger__bar" />
              <span className="admin-hamburger__bar" />
              <span className="admin-hamburger__bar" />
            </button>
          </div>
        </div>
      )}

      {/* Main content */}
      <main className="admin-main">
        <div className="admin-page-title">{currentLabel}</div>
        <div className="admin-page-sub">Değişiklikler Firebase'e kaydedilir ve siteye anında yansır.</div>

        {/* {active !== "settings" && (
          <div className="admin-seed-box">
            <strong style={{color:"var(--text)"}}>Başlangıç verisi:</strong> Boş veritabanı için CV verilerini yükler.{" "}
            <button className="admin-seed-btn" onClick={handleSeed} disabled={seeding}>
              {seeding ? "Yükleniyor..." : "Şimdi Yükle"}
            </button>
          </div>
        )} */}

        {active === "settings" && <SettingsSection />}
        {active === "projects" && <ProjectsSection />}
        {active === "experience" && <ExperienceSection />}
        {active === "skills" && <SkillsSection />}
        {active === "certificates" && <CertificatesSection />}
        {active === "services" && <ServicesSection />}
        {active === "testimonials" && <TestimonialsSection />}
        {active === "messages" && <MessagesSection />}
      </main>
    </div>
  );
}
